import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ShapeXLogo from '../components/ShapeXLogo';
import { Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, Mail } from 'lucide-react';

interface SignUpProps {
  onSuccess: () => void;
  onSwitchToSignIn: () => void;
}

type Step = 'form' | 'verification';

export default function SignUp({ onSuccess, onSwitchToSignIn }: SignUpProps) {
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otp, setOtp] = useState('');

  const { signUp, verifyOtp } = useAuth();

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name || !email || !password || !confirmPassword) {
      setError('Preencha todos os campos.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      setError('A senha precisa incluir pelo menos uma letra e um número.');
      return;
    }

    setLoading(true);
    const { error: signUpError } = await signUp(email, password);
    setLoading(false);

    if (signUpError) {
      if (signUpError.message.includes('already registered')) {
        setError('Não foi possível concluir o cadastro. Verifique suas informações.');
      } else {
        setError(signUpError.message);
      }
    } else {
      // Sucesso no cadastro, ir para verificação
      setStep('verification');
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!otp) {
      setError('Digite o código de verificação.');
      return;
    }

    setLoading(true);
    const { error: verifyError } = await verifyOtp(email, otp);
    setLoading(false);

    if (verifyError) {
      setError('Código inválido ou expirado.');
    } else {
      onSuccess();
    }
  };

  const inputClasses = "w-full rounded-xl bg-[#0D0D0D] border px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#E53935] transition-all placeholder-[#525252]";
  const borderClass = "rgba(255, 255, 255, 0.1)";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#0D0D0D' }}>
      <div className="w-full max-w-[400px] animate-in fade-in zoom-in duration-500">

        {/* Logo Section */}
        <div className="flex flex-col items-center mb-8">
          <ShapeXLogo width={280} className="mb-2" />
          <p className="text-[#A1A1A1] text-xs sm:text-sm text-center tracking-wide">
            “A rotina forja o que a motivação nunca sustentou.”
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-[24px] p-6 sm:p-8 border shadow-2xl backdrop-blur-sm"
          style={{
            backgroundColor: '#141414',
            borderColor: borderClass,
            boxShadow: '0 0 40px rgba(0,0,0,0.5)'
          }}
        >
          {step === 'form' ? (
            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-white font-bold text-lg">Criar conta</h2>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#E53935] shrink-0 mt-0.5" />
                  <p className="text-[#E53935] text-xs font-medium leading-relaxed">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Nome completo"
                  className={inputClasses}
                  style={{ borderColor: error ? '#E53935' : borderClass }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                <input
                  type="email"
                  placeholder="E-mail"
                  className={inputClasses}
                  style={{ borderColor: error ? '#E53935' : borderClass }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Senha"
                    className={inputClasses}
                    style={{ borderColor: error ? '#E53935' : borderClass }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1A1] hover:text-white transition-colors p-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Confirmar senha"
                    className={inputClasses}
                    style={{ borderColor: error ? '#E53935' : borderClass }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1A1] hover:text-white transition-colors p-1"
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  className="w-4 h-4 rounded border-white/20 bg-[#0D0D0D] text-[#E53935] focus:ring-[#E53935] focus:ring-offset-0"
                  required
                />
                <label htmlFor="acceptTerms" className="text-[#A1A1A1] text-xs cursor-pointer select-none">
                  Aceito os termos e a política de privacidade
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#E53935',
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 20px rgba(229, 57, 53, 0.3)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Criando conta...
                  </>
                ) : (
                  'Criar conta'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#E53935]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#E53935]" />
                </div>
                <h2 className="text-white font-bold text-xl mb-2">Verifique seu e-mail</h2>
                <p className="text-[#A1A1A1] text-sm">
                  Enviamos um código de verificação para <br />
                  <span className="text-white font-medium">{email}</span>
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#E53935] shrink-0 mt-0.5" />
                  <p className="text-[#E53935] text-xs font-medium leading-relaxed">{error}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-[#A1A1A1] mb-2 block uppercase tracking-wider">Código de Verificação</label>
                <input
                  type="text"
                  placeholder="000000"
                  className={`${inputClasses} text-center text-2xl tracking-[0.5em] font-mono`}
                  style={{ borderColor: error ? '#E53935' : borderClass }}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#E53935',
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 20px rgba(229, 57, 53, 0.4)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar Código'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('form')}
                className="w-full text-[#A1A1A1] text-xs hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-3 h-3" />
                Voltar e corrigir e-mail
              </button>
            </form>
          )}

          {step === 'form' && (
            <div className="mt-6 text-center">
              <p className="text-[#525252] text-xs">
                Já tem uma conta?{' '}
                <button
                  onClick={onSwitchToSignIn}
                  className="text-[#E53935] font-bold hover:underline hover:text-red-400 transition-colors"
                >
                  Entrar
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
